<template>
  <Sidebar
    v-if="showSidebar"
    color="blue"
    :items="items"
    section-class="atb-p-0"
    :toggle-open-style="{ transform: 'translateX(200px)' }"
    @onMenuItemClick="navigateToSelectedPage"
  >
    <template v-slot="slotProps">
      {{ slotProps.items }}
    </template>
  </Sidebar>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import Sidebar from '@atb-ui/eds-vue/src/components/Sidebar.vue';
  import AccessGuard from '@/app/shared/guards/access-guard';
  import type {
    AppSubjects,
    AMSSubjects,
    AdminSubjects,
    ToolsSubject,
    ViewerSubjects,
  } from '@/app/shared/services/ability-service';
  import { FeatureGuard } from '../guards/feature-guard';
  import { FEATURE_FLAGS } from '../constants/feature-flags';

  type Subjects =
    | AppSubjects
    | AMSSubjects
    | AdminSubjects
    | ToolsSubject
    | ViewerSubjects;

  const getAdminRouterNavigation = () => {
    return [
      {
        label: 'Dashboard',
        icon: 'dashboard',
        link: 'admin-home',
      },
      {
        label: 'User',
        icon: 'perm_identity',
        link: 'admin-users',
        roleAccessKey: 'USER',
      },
      {
        label: 'Client',
        icon: 'business',
        link: 'admin-client',
        roleAccessKey: 'CLIENT',
      },
      {
        label: 'Roles',
        icon: 'admin_panel_settings',
        link: 'admin-role',
        roleAccessKey: 'ROLE',
      },
      {
        label: 'Batch Deploy',
        icon: 'description',
        link: 'admin-batch',
        roleAccessKey: 'BATCH',
        featureKey: FEATURE_FLAGS.BATCH_AGENT,
      },
      {
        label: 'Engine Deploy',
        icon: 'cloud_upload',
        link: 'admin-deploy',
        roleAccessKey: 'DEPLOY',
        featureKey: FEATURE_FLAGS.BUREAU_COMPOSER,
      },
    ];
  };

  const getAMSRouterNavigation = (t: any) => {
    return [
      {
        label: t('dashboard'),
        icon: 'dashboard',
        link: 'ams-home',
      },
      {
        label: t('client'),
        icon: 'business',
        link: 'ams-client',
      },
      {
        label: t('systems'),
        icon: 'folder',
        link: 'ams-systems',
      },
      {
        label: t('attributes'),
        icon: 'low_priority',
        link: 'ams-attributes',
        roleAccessKey: 'ATTRIBUTES_OR_FILTERS_CODE',
      },
      {
        label: t('filters'),
        icon: 'filter_list',
        link: 'ams-filters',
        roleAccessKey: 'ATTRIBUTES_OR_FILTERS_CODE',
      },
      {
        label: t('tools'),
        icon: 'build',
        link: 'ams-tools',
      },
      {
        label: t('creditAttributeTracker'),
        icon: 'create',
        isDisplayed: config.ENABLE_CREDIT_ATTRIBUTE_TRACKER_FEATURE,
        extLink:
          'https://analyticsviz.experian.com/#/site/CAT/views/PremierAttributes1_3UAGReport/Home?:iid=1',
      },
    ];
  };

  export default defineComponent({
    name: 'AppSidebar',
    components: {
      Sidebar,
    },
    computed: {
      items(): any[] {
        const routeName = this.$route.name;
        if (routeName && routeName.toString().startsWith('admin')) {
          return getAdminRouterNavigation().filter(
            (item) =>
              (!item.featureKey ||
                FeatureGuard.isFeatureAllowed(item.featureKey)) &&
              (!item.roleAccessKey ||
                AccessGuard.hasAccess(item.roleAccessKey as Subjects))
          );
        } else {
          return getAMSRouterNavigation(this.$t).filter(
            (item) =>
              !item.roleAccessKey ||
              AccessGuard.hasAccess(item.roleAccessKey as Subjects)
          );
        }
      },
      showSidebar(): boolean {
        return this.$route.name !== 'base-home';
      },
    },
    methods: {
      navigateToSelectedPage(pageName: string): void {
        this.$router.push({ name: pageName });
      },
    },
  });
</script>

<style></style>
